import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import {
  IOrderDetailsRequestParams,
  IOrderDetailsResponse,
} from '../../models/order-details';
import {
  moveCanceledObjectToLastIndex,
  processFulfillmentGroup,
} from '../../utils/order-details/OrderDetailsUtils';
import { OrderDetailsHeader } from '../order-details/OrderDetailsHeader';
import { OrderDetailsMain } from '../order-details/OrderDetailsMain';
import { OrderPaymentInformation } from '../order-details/OrderPaymentInformation';
import { OrderTotal } from '../order-details/OrderTotal';

import { ICancelInfo, ICancellationItem } from '../../models/cancel-items';
import {
  CancellationDrawer,
  ICancellationDrawer,
} from '../shared/cancellation-drawer/CancellationDrawer';
import {
  EditDeliveryInstructionsDrawer,
  IEditDeliveryInstructionsDrawer,
} from '../order-details/EditDeliveryInstructions/EditDeliveryInstructionsDrawer';
import {
  useCommonContext,
  useEditDeliveryInstructionsContext,
} from '../../hooks/useContexts';
import { PTCProvider } from '../../contexts/PTCContext';

export const OnlineOrder = ({
  customerType,
  formData,
}: {
  customerType: string;
  formData: IOrderDetailsResponse | undefined | null;
}) => {
  const { timezone } = useCommonContext();
  const location = useLocation();

  const [cancellationDrawerOpen, setCancellationDrawerOpen] =
    useState<boolean>(false);
  const { setEditDeliveryInstructionsDrawerOpen } =
    useEditDeliveryInstructionsContext();
  const toggleDrawer = () => {
    setCancellationDrawerOpen(false);
    setEditDeliveryInstructionsDrawerOpen(false);
  };
  const [cancelInfo, setCancelInfo] = useState<ICancelInfo>({} as ICancelInfo);
  const [cancellationItems, setCancellationItems] = useState<
    ICancellationItem[]
  >([]);

  if (!formData) {
    return null;
  }

  const encryptedBigReceiptRequestIndex = formData?.encryptedBigReceiptRequests
    ? Object.keys(formData?.encryptedBigReceiptRequests)?.find(
        (key: string) => key.charAt(0) === 'H',
      )
    : '';

  const displayOrderNumber = () => {
    if (
      formData?.orderNumber?.length > 1 &&
      formData?.registerNumber === 0 &&
      formData?.orderNumber?.[0] === 'H'
    ) {
      return formData?.orderNumbers?.[1];
    }
    return formData?.orderNumber ?? formData?.orderNumbers?.[0];
  };

  const destructureFulfillmentGroupForCanceledOrder =
    formData?.fulfillmentGroups &&
    processFulfillmentGroup(formData?.fulfillmentGroups);

  const modifiedFulfillmentGroups =
    destructureFulfillmentGroupForCanceledOrder &&
    moveCanceledObjectToLastIndex(destructureFulfillmentGroupForCanceledOrder);

  const orderDetailsRequestParams: IOrderDetailsRequestParams = {
    orderDetailsRoute: location.pathname,
    orderNumber: formData?.orderNumber ?? formData?.orderNumbers?.[0],
    salesDate: '',
    storeNumber: formData?.storeNumber,
    transactionId: 0,
    registerNumber: 0,
    orderIndex: 0,
    customerAccountId: '',
    timezone,
  };

  const CancellationDrawerProps: ICancellationDrawer = {
    open: cancellationDrawerOpen,
    onClose: toggleDrawer,
    isCancelItemsPage: false,
    cancelAllItems: true,
    orderDetailsData: formData,
    orderDetailsRequestParams,
    cancelInfo,
    drawerLineItems: cancellationItems,
    isPostRemorse: cancelInfo.isPostRemorseCancel, // Here goes the flag for PostRemorse button in the Drawer
    STHorBOSSAlert: cancelInfo.isSTHorBOSSRemorseCancel,
  };

  const EditDeliveryInstructionsDrawerProps: IEditDeliveryInstructionsDrawer = {
    toggleDrawer,
  };

  return (
    <div className="sui-grid sui-grid-cols-1 md:sui-grid-cols-2 lg:gap-6">
      <PTCProvider>
        <OrderDetailsHeader
          orderDetailsData={formData}
          encryptedBigReceiptRequest={
            formData?.encryptedBigReceiptRequests?.[
              Number(encryptedBigReceiptRequestIndex) ?? 0
            ]
          }
          encryptedReceiptRequest={formData?.encryptedReceiptRequest}
          orderNumber={displayOrderNumber()}
          customerType={customerType}
          filteredFulfillmentObject={modifiedFulfillmentGroups}
        />
        <OrderPaymentInformation
          orderDetailsData={formData}
          customerType={customerType}
        />
        <OrderDetailsMain
          fulfillmentGroups={modifiedFulfillmentGroups}
          LineItemsProps={{
            orderDetailsData: formData,
          }}
          orderDetailsData={formData}
          orderDetailsRequestParams={orderDetailsRequestParams}
          orderNumber={formData?.orderNumber ?? formData?.orderNumbers?.[0]}
          setCancelInfo={setCancelInfo}
          setCancellationDrawerOpen={setCancellationDrawerOpen}
          setCancellationItems={setCancellationItems}
          customerType={customerType}
        />
        <OrderTotal
          shippingCharge={formData?.shippingCharge}
          deliveryCharge={formData?.deliveryCharge}
          hasPreferredDiscFlag={formData?.preferredDiscFlg}
          hasProXtraSaving={Boolean(formData?.otherDiscountAmount)}
          orderDetailsData={formData}
          customerType={customerType}
          filteredFulfillmentObject={modifiedFulfillmentGroups}
          militaryDiscount={formData?.militaryDiscount ?? {}}
        />
        <ErrorBoundary name="cancellation-drawer">
          <CancellationDrawer {...CancellationDrawerProps} />
        </ErrorBoundary>
        <ErrorBoundary name="edit-delivery-instructions-drawer">
          <EditDeliveryInstructionsDrawer
            {...EditDeliveryInstructionsDrawerProps}
          />
        </ErrorBoundary>
      </PTCProvider>
    </div>
  );
};
